import { Remoteable, Supertype, supertypeClass } from '@haventech/amorphic';
import { Agent } from '../../../common/js/customer/Agent';
import { Person } from '../../../common/js/customer/Person';
import { PolicyRider } from '../../../common/js/customer/PolicyRider';
import { SafeInviteInfo } from '../../../common/js/customer/SafeInviteInfo';
import {
  SafeAgent,
  SafePolicyRider,
  SafeQuoteRider,
  SafeWholePolicy,
  SafeWholeQuote,
} from '../../../common/js/customer/SafePolicyInfo';
import { QuoteRider } from '../../../common/js/quote/QuoteRider';
import { IllustrationAgent, IllustrationPerson, IllustrationRider } from './IllustrationRequest';
import { Utils } from '../../../common/js/Utils';

import { QuoteForm } from '../../../coverpath-agent/public/js/components/home-new/quote-or-apply-new/QuoteForm';
import { Product, RiderType } from '../../../common/js/static/Product';
import { CoverpathPolicy } from '../../classes/coverpath_subclasses/CoverpathPolicy';
import { CoverpathCustomer } from '../../classes/coverpath_subclasses/CoverpathCustomer';
import { ProductClientController } from '../../js/controllers/ProductClientController';
import { CommonUtils } from '../../../common/js/static/CommonUtils';
import { defaultTaxBracket } from '../../../common/js/constants/policy';
import { RateClasses } from '../../../common/js/static/RateClasses';
import { IllustrationRequest } from '../../classes/wholelife/IllustrationRequest';
import { CoverpathWholeLifeQuote } from '../../classes/quote/coverpath/CoverpathWholeLifeQuote';
import { VSTResult } from '../../../coverpath-common/advisor-sales-tools/interfaces/VST';
import { get } from 'lodash';

@supertypeClass({ toClient: true, toServer: true })
export class IllustrationRequestHelpers extends Remoteable(Supertype) {
  static createIllustrationRiders(policyRiders: PolicyRider[]): IllustrationRider[] {
    const clonedRiders: IllustrationRider[] = [];
    policyRiders.forEach((rider) => {
      // add to clonedRiders if not 1035
      if (!rider.lumpSumUses1035) {
        clonedRiders.push({
          type: rider.type,
          amount: rider.amount,
          premium: rider.premiumBase,
          waiver: rider.premiumWaiver,
          plannedYears: rider.plannedYears,
          paymentFrequency: rider.paymentFrequency,
          accelerationPercent: rider.benefitDisbursementMethod,
          monthlyIncreaseOption: rider.monthlyIncreaseOption,
          puaAddedToBenefitPool: rider.puaAddedToBenefitPool,
          predictedBenefitAge: rider.predictedBenefitAge,
        });
      }
    });
    return clonedRiders;
  }

  private static safePolicyRiderToIllustrationRider(rider: SafePolicyRider): IllustrationRider {
    return {
      type: rider.type,
      amount: rider.amount,
      premium: rider.premiumBase,
      waiver: rider.premiumWaiver,
      plannedYears: rider.plannedYears,
      paymentFrequency: rider.paymentFrequency,
      accelerationPercent: rider.accelerationPercent,
      predictedBenefitAge: rider.predictedBenefitAge,
      puaAddedToBenefitPool: rider.puaAddedToBenefitPool,
      monthlyIncreaseOption: rider.monthlyIncreaseOption,
    };
  }

  private static safeQuoteRiderToIllustrationRider(rider: SafeQuoteRider): IllustrationRider {
    return {
      type: rider.type,
      amount: rider.amount,
      premium: rider.premiumBase,
      waiver: rider.premiumWaiver,
      plannedYears: rider.plannedYears,
      paymentFrequency: rider.paymentFrequency,
      accelerationPercent: rider.accelerationPercent,
      predictedBenefitAge: rider.predictedBenefitAge,
      puaAddedToBenefitPool: rider.puaAddedToBenefitPool,
      monthlyIncreaseOption: rider.monthlyIncreaseOption,
    };
  }

  static createIllustrationForSafeRiders(safePolicy: SafeWholePolicy): IllustrationRider[] {
    const clonedRiders: IllustrationRider[] = [];

    if (safePolicy.hasGIR && !!safePolicy.gir) {
      if (!safePolicy.gir.lumpSumUsers1035) {
        clonedRiders.push(this.safePolicyRiderToIllustrationRider(safePolicy.gir));
      }
    }
    if (safePolicy.hasRTR && !!safePolicy.rtr) {
      if (!safePolicy.rtr.lumpSumUsers1035) {
        clonedRiders.push(this.safePolicyRiderToIllustrationRider(safePolicy.rtr));
      }
    }
    if (safePolicy.hasCIR && !!safePolicy.cir) {
      if (!safePolicy.cir.lumpSumUsers1035) {
        clonedRiders.push(this.safePolicyRiderToIllustrationRider(safePolicy.cir));
      }
    }
    if (safePolicy.hasALIR && !!safePolicy.alir) {
      if (!safePolicy.alir.lumpSumUsers1035) {
        clonedRiders.push(this.safePolicyRiderToIllustrationRider(safePolicy.alir));
      }
    }

    return clonedRiders;
  }

  static createIllustrationRidersForQuote(policyRiders: QuoteRider[]): IllustrationRider[] {
    const clonedRiders: IllustrationRider[] = [];
    policyRiders.forEach((rider) => {
      clonedRiders.push({
        type: rider.type,
        amount: rider.amount,
        premium: rider.premiumBase,
        waiver: rider.premiumWaiver,
        plannedYears: rider.plannedYears,
        paymentFrequency: rider.paymentFrequency,
      });
    });
    return clonedRiders;
  }

  static createIllustrationRidersForSafeQuote(quote: SafeWholeQuote): IllustrationRider[] {
    const clonedRiders: IllustrationRider[] = [];
    if (!!quote.gir) {
      clonedRiders.push(this.safeQuoteRiderToIllustrationRider(quote.gir));
    }
    if (!!quote.rtr) {
      clonedRiders.push(this.safeQuoteRiderToIllustrationRider(quote.rtr));
    }
    if (!!quote.cir) {
      clonedRiders.push(this.safeQuoteRiderToIllustrationRider(quote.cir));
    }
    if (!!quote.alir) {
      clonedRiders.push(this.safeQuoteRiderToIllustrationRider(quote.alir));
    }
    return clonedRiders;
  }

  static createIllustrationForSafeCustomer(safeInviteInfo: SafeInviteInfo) {
    const customer = safeInviteInfo.safePolicyInfo.primaryPolicy.customer;
    return {
      firstName: customer.firstName,
      lastName: customer.lastName,
      gender: customer.gender,
      dob: customer.dob,
    };
  }

  static createIllustrationPerson(person: Person): IllustrationPerson {
    return {
      firstName: person.firstName,
      lastName: person.lastName,
      gender: person.gender,
      dob: person.dob,
    };
  }

  static createIllustrationForSafeAgent(agent: SafeAgent): IllustrationAgent {
    return {
      firstName: agent.firstName,
      lastName: agent.lastName,
      phone: agent.phoneNumber,
      address: {
        street: agent.street,
        city: agent.city,
        state: agent.state,
        zip: agent.zip,
      },
      producerId: agent.producerId,
    };
  }

  static createIllustrationAgent(agent: Agent): IllustrationAgent {
    const address = agent.address;
    return {
      firstName: agent.person.firstName,
      lastName: agent.person.lastName,
      phone: agent.phone ? agent.phone.number : '',
      address: {
        street: address ? address.street : '',
        city: address ? address.city : '',
        state: address ? address.state : '',
        zip: address ? address.zip : '',
      },
      producerId: agent.producerId,
      agentEmail: agent.person.email,
    };
  }

  public static createVSTRequest(customer: CoverpathCustomer): IllustrationRequest {
    const currentDate: Date = new Date();
    const primaryPolicy = customer.application.primaryPolicy as CoverpathPolicy;
    const cpQuote: CoverpathWholeLifeQuote = customer.wholeLifeQuote as CoverpathWholeLifeQuote;
    const faceUnits: number = customer.application.primaryPolicy.faceAmountWithPendingAmendment();
    const dob = customer.application.primaryPolicy.insured.person.dob;
    const waiverUnits: number = customer.application.primaryPolicy.waiver ? faceUnits : 0;
    const hasApplicationAndPolicy = customer.application && customer.application.primaryPolicy;
    let rateClass = RateClasses[cpQuote.healthClass].stringCode;
    const effectiveDate = cpQuote.effectiveDate;
    // If the app and policy exist, determine if the quote rate class can be used
    // or if we need to use standard/standard tobacco instead based on face amount

    const policyFaceAmount = customer.application.primaryPolicy.faceAmount;
    const product = customer.application.primaryPolicy.product;
    const productType = customer.application.primaryPolicy.productType;

    if (hasApplicationAndPolicy) {
      const faceAndRtrAmount = primaryPolicy.hasRtrRider
        ? policyFaceAmount + primaryPolicy.rtrRiderAmount
        : policyFaceAmount;
      rateClass = CommonUtils.determineRatingClass(
        cpQuote.healthClass,
        faceAndRtrAmount,
        product,
        productType
      ).stringCode;
    }

    const hasExchange1035 = primaryPolicy.alirRider && primaryPolicy.alirRider.lumpSumUses1035;

    return {
      requestId: Utils.generateUuid(),
      dividendOption: cpQuote.dividend,
      startDate: effectiveDate || currentDate,
      asOfDate: effectiveDate || currentDate,
      gender: customer.application.primaryPolicy.insured.person.gender,
      dob: dob,
      productCode: customer.application.primaryPolicy.product,
      state: cpQuote.contractState,
      rateClass: rateClass,
      paymentFrequency: Utils.toCapitalizedWords(customer.application.primaryPolicy.paymentFrequency),
      faceAmount: faceUnits,
      waiverUnits: waiverUnits,
      flatExtra: cpQuote.flatExtra,
      taxRate: Utils.round(cpQuote.taxBracket / 100, 2),
      loanRateType: cpQuote.loanType,
      dividendRatePercent: 1,
      dividendInterestAdjustment: cpQuote.dividendInterestAdjustment,
      customerPerson: IllustrationRequestHelpers.createIllustrationPerson(customer.primaryCustomer.person),
      agent: null,
      riders: IllustrationRequestHelpers.createIllustrationRiders(primaryPolicy.riders),
      cashValueTransfer: hasExchange1035 ? primaryPolicy.alirRider.exchange1035.cashValueTransfer : 0,
      costBasis: hasExchange1035 ? primaryPolicy.alirRider.exchange1035.costBasis : 0,
      expectedArrival: hasExchange1035 ? primaryPolicy.alirRider.exchange1035.expectedArrival : 0,
      applyTowards: hasExchange1035 ? primaryPolicy.alirRider.exchange1035.applyTowards : null,
    };
  }

  public static createSafeVSTRequest(safeInviteInfo: SafeInviteInfo): IllustrationRequest {
    const currentDate: Date = new Date();

    const primaryPolicy = safeInviteInfo.safePolicyInfo.primaryPolicy as SafeWholePolicy;
    const faceUnits: number = primaryPolicy.faceAmount;
    const dob = primaryPolicy.insured.dob;
    const waiverUnits: number = primaryPolicy.hasWOP ? faceUnits : 0;
    const policyFaceAmount = primaryPolicy.faceAmount;
    const product = primaryPolicy.product;
    const productType = primaryPolicy.productType;
    const cpQuote = safeInviteInfo.safeWholeQuote;
    const cpTermQuote = safeInviteInfo.safeTermQuote;
    const productSwitchedFromTermToWhole = safeInviteInfo.safePolicyInfo.hasWhole && safeInviteInfo.hasTermQuote;
    let rateClass;
    if (cpQuote || productSwitchedFromTermToWhole) {
      const faceAndRtrAmount = primaryPolicy.hasRTR ? policyFaceAmount + primaryPolicy.rtr.amount : policyFaceAmount;
      rateClass = CommonUtils.determineRatingClass(
        productSwitchedFromTermToWhole ? cpTermQuote.healthClass : cpQuote.healthClass,
        faceAndRtrAmount,
        product,
        productType
      ).stringCode;
    } else {
      rateClass = RateClasses.find(primaryPolicy.healthClass).stringCode;
    }
    // If the app and policy exist, determine if the quote rate class can be used
    // or if we need to use standard/standard tobacco instead based on face amount

    return {
      requestId: Utils.generateUuid(),
      dividendOption: primaryPolicy.dividendOption,
      startDate: primaryPolicy.effectiveDate || currentDate,
      asOfDate: primaryPolicy.effectiveDate || currentDate,
      gender: primaryPolicy.insured.gender,
      dob: dob,
      productCode: primaryPolicy.product,
      state: primaryPolicy.contractState,
      rateClass: rateClass,
      paymentFrequency: Utils.toCapitalizedWords(primaryPolicy.paymentFrequency),
      faceAmount: faceUnits,
      waiverUnits: waiverUnits,
      flatExtra: primaryPolicy.flatExtra,
      taxRate: cpQuote ? Utils.round(cpQuote.taxBracket / 100, 2) : CoverpathWholeLifeQuote.defaultTaxBracket,
      loanRateType: primaryPolicy.loanType,
      dividendRatePercent: 1,
      dividendInterestAdjustment: cpQuote ? cpQuote.dividendInterestAdjustment : 0,
      customerPerson: IllustrationRequestHelpers.createIllustrationForSafeCustomer(safeInviteInfo),
      agent: null,
      riders: IllustrationRequestHelpers.createIllustrationForSafeRiders(primaryPolicy),
      cashValueTransfer: primaryPolicy.hasExchange1035 ? primaryPolicy.exchange1035.cashValueTransfer : 0,
      costBasis: primaryPolicy.hasExchange1035 ? primaryPolicy.exchange1035.costBasis : 0,
      expectedArrival: primaryPolicy.hasExchange1035 ? primaryPolicy.exchange1035.expectedArrival : 0,
      applyTowards: primaryPolicy.hasExchange1035 ? primaryPolicy.exchange1035.applyTowards : null,
    };
  }

  public static createReactiveVSTRequest(quote: QuoteForm): IllustrationRequest {
    const currentDate: Date = new Date();
    const { clientInformation, productType } = quote;
    const wholeLifeQuote = productType === 'Whole' ? quote.wholeLifeQuote : quote.jointAppQuote.wholeLifeQuote;
    const effectiveDate = wholeLifeQuote.effectiveDate;
    const riders = wholeLifeQuote.riders;
    const faceAndRtrAmount = riders?.rtr?.enabled
      ? wholeLifeQuote.faceAmount + riders.rtr.amount
      : wholeLifeQuote.faceAmount;
    const rateClass = CommonUtils.determineRatingClass(
      clientInformation.healthClass,
      faceAndRtrAmount,
      wholeLifeQuote.product,
      quote.productType
    ).stringCode;
    const ridersArray = [];
    if (riders.gir.enabled) {
      ridersArray.push({ type: RiderType.GIR, amount: riders.gir.amount });
    }
    if (riders.rtr.enabled) {
      ridersArray.push({ type: RiderType.RTR, amount: riders.rtr.amount });
    }
    if (riders.alir.enabled) {
      ridersArray.push({
        type: RiderType.ALIR,
        amount: riders.alir.amount,
        plannedYears: riders.alir.plannedYears,
        paymentFrequency: riders.alir.paymentFrequency,
      });
    }
    if (riders.cir.enabled) {
      ridersArray.push({
        type: RiderType.CIR,
        amount: riders.cir.amount,
        premium: riders.cir.premium,
        plannedYears: riders.cir.plannedYears,
        paymentFrequency: riders.cir.paymentFrequency,
        accelerationPercent: riders.cir.benefitDisbursementMethod,
        monthlyIncreaseOption: riders.cir.monthlyIncreaseOption,
        puaAddedToBenefitPool: riders.cir.puaAddedToBenefitPool,
      });
    }
    const hasExchange1035: boolean = wholeLifeQuote.exchange1035.enabled;
    return {
      requestId: Utils.generateUuid(),
      dividendInterestAdjustment: wholeLifeQuote.dividendInterestAdjustment,
      dividendOption: wholeLifeQuote.dividendOption,
      startDate: effectiveDate || currentDate,
      gender: clientInformation.gender,
      dob: clientInformation.dob,
      productCode: wholeLifeQuote.product,
      state: clientInformation.contractState,
      rateClass,
      paymentFrequency: wholeLifeQuote.paymentFrequency,
      faceAmount: wholeLifeQuote.faceAmount,
      waiverUnits: wholeLifeQuote.waiverOfPremium ? wholeLifeQuote.faceAmount : 0,
      flatExtra: 1, // Hmmmmmmmm!
      taxRate: Utils.round(wholeLifeQuote.taxBracket / 100, 2),
      loanRateType: wholeLifeQuote.loanType,
      dividendRatePercent: 1,
      agent: null,
      customerPerson: null,
      riders: ridersArray,
      asOfDate: effectiveDate || currentDate,
      cashValueTransfer: hasExchange1035 ? wholeLifeQuote.exchange1035.cashValueTransfer : 0,
      costBasis: hasExchange1035 ? wholeLifeQuote.exchange1035.costBasis : 0,
      expectedArrival: hasExchange1035 ? wholeLifeQuote.exchange1035.expectedArrival : 0,
      applyTowards: hasExchange1035 ? wholeLifeQuote.exchange1035.applyTowards : null,
    };
  }

  // TODO: Remove placeholder text for the tooltips with the actual content.
  public static createVSTResult(vstData): VSTResult | null {
    const policy = get(vstData, 'data.illustration.payload.policy');
    if (policy) {
      const yearZero = policy.illustrationList[0];

      const vstResult: VSTResult = {
        ages: [yearZero.ageEndYear - 1],
        currentPaidPremiums: [0],
        totalDeathBenefits: {
          name: 'Total Death Benefit, Non-Guaranteed',
          dashStyle: 'Solid',
          color: '#009CDE',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.totalDeathBenefitEndYear)],
        },
        totalCashValues: {
          name: 'Total Cash Value, Non-Guaranteed',
          dashStyle: 'Solid',
          color: '#F5E500',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.totalCashValueEndYear)],
        },
        guaranteedDeathBenefits: {
          name: 'Guaranteed, Death Benefit',
          dashStyle: 'ShortDot',
          color: '#009CDE',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.guaranteedDeathBenefitEndYear)],
        },
        guaranteedCashValues: {
          name: 'Guaranteed, Cash Value',
          dashStyle: 'ShortDot',
          color: '#F5E500',
          tooltip: 'Placeholder Copy',
          data: [Math.round(yearZero.guaranteedCashValueEndYear)],
        },
        accumulatedPaidPremium: {
          name: 'Cumulative Contract Premium',
          dashStyle: 'Solid',
          color: '#43B02A',
          tooltip: 'Placeholder Copy',
          data: [0],
        },
        mecYear: policy.mecYear,
      };

      const dataSet = policy.illustrationList;
      for (let i = 0; i < dataSet.length; i++) {
        vstResult.ages.push(dataSet[i].ageEndYear);
        vstResult.totalDeathBenefits.data.push(Math.round(dataSet[i].totalDeathBenefitEndYear));
        vstResult.totalCashValues.data.push(Math.round(dataSet[i].totalCashValueEndYear));
        vstResult.guaranteedDeathBenefits.data.push(Math.round(dataSet[i].guaranteedDeathBenefitEndYear));
        vstResult.guaranteedCashValues.data.push(Math.round(dataSet[i].guaranteedCashValueEndYear));
        vstResult.currentPaidPremiums.push(dataSet[i].contractPremiumBegYear);
        const premium =
          vstResult.accumulatedPaidPremium.data[i] + Math.round(dataSet[i].guaranteedContractPremiumBegYear);
        vstResult.accumulatedPaidPremium.data.push(premium);
      }
      return vstResult;
    }
  }
}
